import type { AppError, ErrorKind } from '@/shared/model/types/error';
import {
  ERROR_MESSAGE_BY_KIND,
  NO_RESPONSE_STATUS,
} from '@/shared/constants/errorConstants';
import { getErrorStatus } from '@/shared/utils/getErrorStatus';
import { getErrorFields } from '@/shared/utils/getErrorFields';
import { isRecord } from '@/shared/utils/isRecord';
import { isApiError } from '@/shared/utils/isApiError';
import type { ApiError } from '@/shared/model/types/http';

export const getKindByStatus = (status: number): ErrorKind => {
  if (status === 401) return 'auth';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'notFound';
  if (status === 422 || status === 400) return 'validation';
  if (status >= 500) return 'server';
  return 'unknown';
};

export const getServerMessage = (error: ApiError): string =>
  error.response.data?.message ?? '';

const NETWORK_ERROR_NAMES = new Set(['FetchError', 'AbortError']);

const isNetworkFlavored = (error: Error): boolean =>
  NETWORK_ERROR_NAMES.has(error.name) &&
  !('statusCode' in error && typeof error.statusCode === 'number');

const isNetworkError = (error: unknown): boolean => {
  if (error instanceof Error) return isNetworkFlavored(error);

  if (isRecord(error) && error.cause instanceof Error) {
    return isNetworkFlavored(error.cause);
  }

  return false;
};

export const normalizeError = (error: unknown): AppError => {
  const isNetwork = isNetworkError(error);

  const status = isNetwork
    ? NO_RESPONSE_STATUS
    : getErrorStatus(error, NO_RESPONSE_STATUS);

  const kind = isNetwork
    ? 'network'
    : status === NO_RESPONSE_STATUS
      ? 'unknown'
      : getKindByStatus(status);

  const rawFields = getErrorFields(error);
  const fields = Object.entries(rawFields).reduce<Record<string, string>>(
    (accumulator, [key, value]) => {
      const text = Array.isArray(value) ? value[0] : value;

      accumulator[key] =
        typeof text === 'string' ? text : text == null ? '' : String(text);

      return accumulator;
    },
    {},
  );

  const serverMessage = isApiError(error) ? getServerMessage(error) : '';
  const canUseServerMessage = status >= 400 && status < 500;
  const message =
    canUseServerMessage && serverMessage ? serverMessage : ERROR_MESSAGE_BY_KIND[kind];

  return { kind, status, message, fields, cause: error };
};
